import React, { useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import Accordion from "src/components/Accordion";
import Loader from "src/components/Loader";
import LovBusinessGroups from "src/components/Lov/BusinessGroups";
import PageTitle from "src/components/PageTitle";
import Title from "src/components/Title";
import { useAuth } from "src/context/Auth/context";
import { useTranslation } from "src/context/Translations/context";
import useEffectFetchData from "src/hooks/useEffectFetchData";
import formatDate from "src/utils/formatDate";
import { v4 } from "uuid";
import { IContracts } from "../Config/BusinessGroup/interface";
import Card from "./components/Card";
import { Eftba001002 } from "./interface";

const MyBusinessGroup: React.FC = () => {
	const {translate, currentLanguage} = useTranslation();
	const {businessGroup, superUser} = useAuth();
	const {REACT_APP_S3_ADDRESS, REACT_APP_S3_DIRECTORY} = process.env;
	const [internalEfa001Id, setInternalEfa001Id] = useState(businessGroup.efa001Id);
	
	const { control } = useForm();
	const {data , loading, errorFetching } = useEffectFetchData<Eftba001002> (
		"BusinessGroup/GetInfosMyBusinessGroup", {
			efa001Id: internalEfa001Id
		},
		undefined,
		[internalEfa001Id]
	);

	return (
		<>
			<PageTitle
				title={translate("Meu grupo empresarial")}
				icon={{
					name: "building"
				}}
			>
				{superUser && (
					<LovBusinessGroups 
						control={control}
						name="group" 
						defaultValue={businessGroup ? {
							value: businessGroup.efa001Id.toString(),
							label: businessGroup.efa001Fantasia.toString()
						} : undefined}
						label={translate("Grupo de Empresas")}
						onChangeCapture={(e: any) => setInternalEfa001Id(Number(e.value))}
						styleInline="width: 320px;"
					/>
				)}
			</PageTitle>
			{errorFetching || (!loading && !data) ? (
				<RCCAlert 
					title={translate("Atenção")}
					description={translate("Erro ao carregar dados")}
				/>
			) : (
				<>
					{loading ? <Loader /> : (
						<>
							<RCCGrid container item alignItems="center" justifyContent="center">
								<RCCGrid container item justifyContent="center">
									<Title color="purple" size="lg">
										{data.efa001Fantasia}
									</Title>
								</RCCGrid>
								<RCCGrid container item>
									<Title color="purple" size="lg">
										{translate("Contrato")}
									</Title>
									<hr/>
								</RCCGrid>
								{data.eftbe001 && (
									<RCCGrid container item justifyContent="center">
										<RCCGrid container direction="column">
											<Title color="defaultColor" size="sm">
												<strong>
													{translate("Plano comercial")} 
												</strong>
											&nbsp; {data.eftbe001.efe001Efd001Descricao}
											</Title>
											<Title color="defaultColor" size="sm">
												<strong>
													{translate("Vigência")} 
												</strong>
												&nbsp; 
												{formatDate(data.eftbe001.efe001Dtinicio, currentLanguage)} - {formatDate(data.eftbe001.efe001Dtfim, currentLanguage)}
											</Title>
											<Title color="defaultColor" size="sm">
												<strong>
													{translate("Data último acesso")} 
												</strong>
												&nbsp; 
												{data.efs002DtLogon ? formatDate(data.efs002DtLogon, currentLanguage, true) : translate("sem registros")}
												<Link to={`/systemconnections/?businessgroup=${internalEfa001Id}`}>
													<RCCTooltip
														text={translate("Conexões do sistema")}
														color="grey"
													>
														<RCCButton 
															color="transparent"
															style={{ padding: "0px 10px" }}
															icon={{
																name: "cloud",
																size: 14,
																color: "var(--input-font-color)"
															}}
														/>
													</RCCTooltip>
												</Link>
											</Title>
										</RCCGrid>
										<RCCGrid container item spacing={1} justifyContent="center" style={{margin: "0.5rem 0"}}>
											{data.eftba012 && data.eftba012.length > 0 && data.eftba012.filter((x) => x.efa012Valor > 0).map((contract: IContracts) => (
												<RCCGrid key={v4()} container item lg={3} md={4} sm={6} xs={12}>
													<Card 
														title={translate(contract.efa016Descricao)}
														totalQuantity={contract.efa012Valor}
														quantity={contract.quantityUtilized}
													/>
												</RCCGrid>
											))}
										</RCCGrid>
									</RCCGrid>
								)}
								<RCCGrid container item spacing={1}>			
									<RCCGrid container item sm={12} md={6} style={{ alignItems: "flex-start", height: "fit-content" }}>
										<RCCGrid container item>
											<Title color="purple" size="lg">
												{translate("Itens do contrato")}
											</Title>
											<hr/>
										</RCCGrid>
										<RCCGrid container item spacing={1}>
											{data.eftba012 && data.eftba012.length > 0 && data.eftba012.filter((x) => x.efa012Valor <= 0).map((contract: IContracts) => (
												<RCCGrid key={v4()} container item sm={6} xs={12}>
													<Card 
														title={translate(contract.efa016Descricao)}
														totalQuantity={contract.efa012Valor}
														quantity={contract.quantityUtilized}
													/>
												</RCCGrid>
											))}
										</RCCGrid>
									</RCCGrid>
									<RCCGrid container item sm={12} md={6} style={{ alignItems: "flex-start", height: "fit-content" }}>
										<RCCGrid container item>
											<Title color="purple" size="lg">
												{translate("Dados")}
											</Title>
											<hr/>
										</RCCGrid>
										<RCCGrid container item spacing={1}>
											{data.data.map((dt) => (
												<RCCGrid key={v4()} container item md={4} sm={6} xs={12}>
													<Card 
														title={translate(dt.title)}
														totalQuantity={0}
														quantity={dt.total ?? 0}
														showQuantityZero
														colorProp="purple"
													/>
												</RCCGrid>
											))}						
										</RCCGrid>
									</RCCGrid>
								</RCCGrid>
								{data.eftba002 && data.eftba002.length > 0 && (
									<>
										<RCCGrid container item>
											<Title color="purple" size="lg">
												{translate("Empresas")}
											</Title>
											<hr  style={{ marginBottom: 0 }}/>
										</RCCGrid>
										{data.eftba002.map((company) => 
											<Accordion
												key={v4()} 
												header={<RCCGrid container item>
													<RCCGrid container item xs={12} sm={10}>
														<div style={{width: "300px", display: "flex", alignItems: "center"}}>
															{company.efa002Imagem && company.efa002Imagem !== null && (
																<img 
																	className="company-img-header" 
																	src={`${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_empresas/${company.efa002Imagem}`} 
																	alt="Company Logo"
																	style={{ maxWidth: "200px" }}
																/>
															)}
														</div>
														<Title color="defaultColor" size="sm" bold>
															{company.efa002Fantasia}
														</Title>
													</RCCGrid>
													<Title color="defaultColor" size="sm">
														{company.efa002Cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5")}
													</Title>
												</RCCGrid>}
											>
												<RCCGrid container item>
													{company.eftba012 && company.eftba012.length > 0 && <>
														<RCCGrid container item>
															<Title color="purple" size="lg">
																{translate("Itens do contrato")}
															</Title>
															<hr />
														</RCCGrid>
														<RCCGrid container item spacing={1}>
															{company.eftba012.map((contract: IContracts) => (
																<RCCGrid key={v4()} container item lg={3} md={4} sm={6} xs={12}>
																	<Card 
																		title={translate(contract.efa016Descricao)}
																		totalQuantity={contract.efa012Valor}
																		quantity={contract.quantityUtilized}
																	/>
																</RCCGrid>
															))}
														</RCCGrid>
													</>}
													<RCCGrid container item>
														<Title color="purple" size="lg">
															{translate("Dados")}
														</Title>
														<hr/>
													</RCCGrid>
													<RCCGrid container item spacing={1}>
														{company.data.map((dt) => (
															<RCCGrid key={v4()} container item lg={3} md={4} sm={6} xs={12}>
																<Card 
																	title={translate(dt.title)}
																	totalQuantity={data.data.find((x) => x.title === dt.title)?.total ?? 0}
																	quantity={dt.total ?? 0}
																	showQuantityZero
																	colorProp="purple"
																/>
															</RCCGrid>
														))}
													</RCCGrid>
												</RCCGrid>
											</Accordion>)}
										
									</>
								)}
							</RCCGrid>
						</>
					)}
				</>
			)}
		</>
	);
};

export default MyBusinessGroup;
